import { useState, useCallback, useRef } from "react"
import { Input } from "antd"
import MicrophoneButton from "../components/MicrophoneButton"
import "../assets/styles/SeccionPaciente.css"
import { useTranslation } from "react-i18next"

const { TextArea } = Input

/**
 * Component for managing and displaying observations in ecographic examinations.
 * Allows recording of observations with speech-to-text functionality.
 *
 * @param {Object} props - Component props
 * @param {Array} props.observations - Array of observation strings
 * @param {Function} props.setObservations - Function to update the observations array
 * @returns {JSX.Element} The ObservationsSectionEcograph component
 */
export const ObservationsSectionEcograph = ({ observations, setObservations }) => {
  const { t } = useTranslation()
  const [currentTranscript, setCurrentTranscript] = useState("")
  const [isMicrophoneActive, setIsMicrophoneActive] = useState(false)
  const isTranscribingRef = useRef(false)

  /**
   * Handles updates to the speech recognition transcript.
   *
   * @param {string} transcript - The current transcript from speech recognition
   */
  const handleTranscriptUpdate = useCallback((transcript) => {
    setCurrentTranscript(transcript)
    isTranscribingRef.current = true
  }, [])

  /**
   * Handles the completion of a speech recognition transcript.
   *
   * @param {string} finalTranscript - The final transcript from speech recognition
   */
  const handleTranscriptComplete = useCallback(
    (finalTranscript) => {
      setObservations((prevObservations) => {
        const updatedObservations = [...prevObservations]
        if (isTranscribingRef.current) {
          if (
            updatedObservations.length > 0 &&
            updatedObservations[updatedObservations.length - 1] === currentTranscript
          ) {
            updatedObservations[updatedObservations.length - 1] = finalTranscript
          } else {
            updatedObservations.push(finalTranscript)
          }
        }
        return updatedObservations
      })
      setCurrentTranscript("")
      isTranscribingRef.current = false
    },
    [currentTranscript, setObservations],
  )

  /**
   * Handles changes to the observations text area.
   *
   * @param {Object} e - The change event
   */
  const handleTextAreaChange = (e) => {
    setCurrentTranscript("")
    const newObservations = e.target.value.split("\n")
    setObservations(newObservations)
  }

  /**
   * Trims whitespace from observations when the text area loses focus.
   */
  const handleTextAreaBlur = () => {
    setObservations((prevObservations) => prevObservations.map((obs) => obs.trim()))
  }

  return (
    <div style={{ position: "relative" }}>
      <TextArea
        placeholder={t("observationsSection.addedObservations")}
        value={observations.join("\n") + (currentTranscript ? `\n${currentTranscript}` : "")}
        rows={4}
        onChange={handleTextAreaChange}
        onBlur={handleTextAreaBlur}
        className="custom-textarea"
        style={{
          paddingRight: "30px",
        }}
      />
      <MicrophoneButton
        onTranscriptUpdate={handleTranscriptUpdate}
        onTranscriptComplete={handleTranscriptComplete}
        isActive={isMicrophoneActive}
        setIsActive={setIsMicrophoneActive}
      />
    </div>
  )
}